/**
 * Bot Monitor Page
 * View bot activity logs and real-time status
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { botsService, type BotLog } from '../services/bots.service'
import { formatDate, formatCurrency } from '../utils/formatters'
import BotStatusChart from '../components/BotStatusChart'

export default function BotMonitorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const botId = parseInt(id || '0')

  const [logLevel, setLogLevel] = useState<string>('all')
  const [logCategory, setLogCategory] = useState<string>('all')
  const [snapshotHours, setSnapshotHours] = useState<number>(24)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)

  // Fetch bot details
  const { data: bot } = useQuery({
    queryKey: ['bot', botId],
    queryFn: () => botsService.get(botId),
    enabled: botId > 0,
  })

  // Fetch bot status (real-time)
  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['bot-status', botId],
    queryFn: () => botsService.getStatus(botId),
    enabled: botId > 0,
    refetchInterval: 10000, // Refresh every 10 seconds
  })

  // Fetch bot logs
  const { data: logsData, refetch: refetchLogs } = useQuery({
    queryKey: ['bot-logs', botId, logLevel, logCategory],
    queryFn: () =>
      botsService.getLogs(botId, {
        level: logLevel === 'all' ? undefined : (logLevel as any),
        category: logCategory === 'all' ? undefined : (logCategory as any),
        limit: 100,
      }),
    enabled: botId > 0,
    refetchInterval: 5000, // Refresh every 5 seconds
  })

  // Fetch bot snapshots (historical data)
  const { data: snapshotsData, refetch: refetchSnapshots } = useQuery({
    queryKey: ['bot-snapshots', botId, snapshotHours],
    queryFn: () => botsService.getSnapshots(botId, { hours: snapshotHours }),
    enabled: botId > 0,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Handle manual refresh
  const handleRefresh = async () => {
    if (isRefreshing) return

    setIsRefreshing(true)
    try {
      await botsService.executeCycle(botId)
      // Refetch all data after cycle execution
      await Promise.all([refetchStatus(), refetchLogs(), refetchSnapshots()])
    } catch (error) {
      console.error('Failed to refresh bot data:', error)
      alert('刷新失敗，請稍後再試')
    } finally {
      setIsRefreshing(false)
    }
  }

  if (botId === 0) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.error}>無效的 Bot ID</div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <button onClick={() => navigate('/bots')} style={styles.backButton}>
              ← 返回
            </button>
            <h1 style={styles.title}>{bot?.name || 'Bot 監控'}</h1>
            <p style={styles.subtitle}>
              {bot?.currency} · {bot?.status === 'active' ? '運行中' : '已停止'}
            </p>
          </div>
          <button onClick={handleRefresh} disabled={isRefreshing} style={styles.refreshDataButton}>
            {isRefreshing ? '刷新中...' : '🔄 刷新資料'}
          </button>
        </div>

        {/* Status Cards */}
        {status && (
          <div style={styles.statusGrid}>
            <StatusCard label="錢包餘額" value={formatCurrency(status.wallet_balance, status.currency)} color="#2563eb" />
            <StatusCard label="掛單數量" value={status.active_offers.toString()} color="#8b5cf6" />
            <StatusCard label="活躍貸款" value={status.active_loans.toString()} color="#10b981" />
            <StatusCard label="總放貸" value={formatCurrency(status.total_lent, status.currency)} color="#f59e0b" />
            <StatusCard label="總收益" value={formatCurrency(status.total_earned, status.currency)} color="#ec4899" />
            <StatusCard
              label="最後更新"
              value={status.last_updated ? formatDate(status.last_updated) : '未更新'}
              color="#6b7280"
            />
          </div>
        )}

        {/* Error Alert */}
        {status?.last_error && (
          <div style={styles.errorAlert}>
            <span style={styles.errorIcon}>⚠️</span>
            <div>
              <p style={styles.errorTitle}>最後錯誤</p>
              <p style={styles.errorMessage}>{status.last_error.message}</p>
              <p style={styles.errorTime}>{formatDate(status.last_error.timestamp)}</p>
            </div>
          </div>
        )}

        {/* Logs Section */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>活動日誌</h2>
            <div style={styles.filters}>
              <select value={logLevel} onChange={(e) => setLogLevel(e.target.value)} style={styles.select}>
                <option value="all">所有級別</option>
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </select>

              <select value={logCategory} onChange={(e) => setLogCategory(e.target.value)} style={styles.select}>
                <option value="all">所有分類</option>
                <option value="api_call">API 調用</option>
                <option value="order">訂單操作</option>
                <option value="loan">貸款事件</option>
                <option value="error">錯誤</option>
                <option value="system">系統</option>
              </select>

              <button onClick={() => refetchLogs()} style={styles.refreshButton}>
                刷新
              </button>
            </div>
          </div>

          <div style={styles.logsContainer}>
            {logsData && logsData.logs.length > 0 ? (
              logsData.logs.map((log) => <LogItem key={log.id} log={log} />)
            ) : (
              <div style={styles.emptyLogs}>暫無日誌記錄</div>
            )}
          </div>

          {logsData && logsData.pagination.has_more && (
            <div style={styles.pagination}>
              <p style={styles.paginationText}>
                顯示 {logsData.pagination.offset + 1} - {logsData.pagination.offset + logsData.logs.length} / 總共{' '}
                {logsData.pagination.total} 條
              </p>
            </div>
          )}
        </div>

        {/* Snapshots Chart */}
        {snapshotsData && (
          <BotStatusChart
            snapshots={snapshotsData.snapshots}
            currency={bot?.currency || 'USD'}
            timeRange={snapshotHours}
            onTimeRangeChange={setSnapshotHours}
          />
        )}
      </div>
    </Layout>
  )
}

/**
 * Status Card Component
 */
function StatusCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ ...styles.statusCard, borderLeftColor: color }}>
      <p style={styles.statusLabel}>{label}</p>
      <p style={styles.statusValue}>{value}</p>
    </div>
  )
}

/**
 * Log Item Component
 */
function LogItem({ log }: { log: BotLog }) {
  const levelColor = {
    debug: '#6b7280',
    info: '#2563eb',
    warning: '#f59e0b',
    error: '#ef4444',
  }[log.level]

  const levelIcon = {
    debug: '🔍',
    info: 'ℹ️',
    warning: '⚠️',
    error: '❌',
  }[log.level]

  const categoryLabel = {
    api_call: 'API',
    order: '訂單',
    loan: '貸款',
    error: '錯誤',
    system: '系統',
  }[log.category]

  return (
    <div style={styles.logItem}>
      <div style={styles.logHeader}>
        <div style={styles.logMeta}>
          <span style={{ ...styles.logLevel, color: levelColor }}>{levelIcon} {log.level.toUpperCase()}</span>
          <span style={styles.logCategory}>{categoryLabel}</span>
          <span style={styles.logTime}>{formatDate(log.created_at)}</span>
        </div>
      </div>
      <p style={styles.logMessage}>{log.message}</p>
      {log.details && (
        <details style={styles.logDetails}>
          <summary style={styles.logDetailsSummary}>查看詳情</summary>
          <pre style={styles.logDetailsContent}>{JSON.stringify(log.details, null, 2)}</pre>
        </details>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '40px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: '#f3f4f6',
    color: '#333',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  refreshDataButton: {
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  error: {
    padding: '20px',
    backgroundColor: '#fee2e2',
    borderRadius: '5px',
    color: '#dc2626',
    textAlign: 'center',
  },
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statusCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    borderLeft: '4px solid',
  },
  statusLabel: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px',
  },
  statusValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
  },
  errorAlert: {
    padding: '16px',
    backgroundColor: '#fee2e2',
    borderRadius: '8px',
    marginBottom: '30px',
    display: 'flex',
    gap: '12px',
    border: '1px solid #fecaca',
  },
  errorIcon: {
    fontSize: '20px',
  },
  errorTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: '4px',
  },
  errorMessage: {
    fontSize: '13px',
    color: '#dc2626',
    marginBottom: '4px',
  },
  errorTime: {
    fontSize: '12px',
    color: '#991b1b',
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '24px',
    marginBottom: '30px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
  },
  filters: {
    display: 'flex',
    gap: '12px',
  },
  select: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    outline: 'none',
    cursor: 'pointer',
  },
  refreshButton: {
    padding: '8px 16px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  logsContainer: {
    maxHeight: '600px',
    overflowY: 'auto',
  },
  emptyLogs: {
    padding: '40px',
    textAlign: 'center',
    color: '#999',
    fontSize: '14px',
  },
  logItem: {
    padding: '16px',
    borderBottom: '1px solid #f3f4f6',
  },
  logHeader: {
    marginBottom: '8px',
  },
  logMeta: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  logLevel: {
    fontSize: '12px',
    fontWeight: '600',
  },
  logCategory: {
    fontSize: '12px',
    padding: '2px 8px',
    backgroundColor: '#f3f4f6',
    borderRadius: '3px',
    color: '#666',
  },
  logTime: {
    fontSize: '12px',
    color: '#999',
  },
  logMessage: {
    fontSize: '14px',
    color: '#333',
    marginBottom: '8px',
  },
  logDetails: {
    marginTop: '8px',
  },
  logDetailsSummary: {
    fontSize: '12px',
    color: '#2563eb',
    cursor: 'pointer',
    userSelect: 'none',
  },
  logDetailsContent: {
    marginTop: '8px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#333',
    overflow: 'auto',
  },
  pagination: {
    marginTop: '16px',
    textAlign: 'center',
  },
  paginationText: {
    fontSize: '13px',
    color: '#666',
  },
}
